import React from 'react'

import { Badge, Box, Button, Flex, HStack } from '@chakra-ui/react'
import { generatePath, Link } from 'react-router'
import { Add, MessageWarning } from 'src/components/icon'
import { LinkTable } from 'src/components/LinkTable/LinkTable'
import { LoadingDots } from 'src/components/LoadingDots/LoadingDots'
import { Paths } from 'src/constants/paths'
import { useSelectedTrial } from 'src/features/auth/context'

import { getIcfDocumentSetDetailRoute } from '../../routes'
import { IcfDocumentSetForList } from '../entity'

type Props = {
  icfDocumentSetList: IcfDocumentSetForList[] | undefined
  canAddDocumentSet: boolean
  selectedTrialHospitalUid: string
}

export const IcfDocumentSetListRenewal: React.FC<Props> = ({
  icfDocumentSetList,
  canAddDocumentSet,
  selectedTrialHospitalUid,
}) => {
  const { selectedTrial } = useSelectedTrial()

  return (
    <Flex direction="column" h="full">
      {canAddDocumentSet && (
        <Flex w="full" justify="end">
          <Button
            leftIcon={<Add />}
            variant="outline"
            as={Link}
            to={generatePath(Paths.IcfDocumentSetNew, {
              trialUid: selectedTrial.uid,
            })}
          >
            新規文書セット作成
          </Button>
        </Flex>
      )}
      <Box flex="1" overflow="auto" mt="3">
        {!!icfDocumentSetList ? (
          <LinkTable
            data={icfDocumentSetList}
            columns={[
              {
                key: 'status',
                renderHeader: 'ステータス',
              },
              {
                key: 'numberingId',
                renderHeader: '文書セットID',
              },
              {
                key: 'name',
                renderHeader: '文書セット名',
              },
              {
                key: 'hasRequiredAgreementForm',
                renderHeader: '同意必須文書',
              },
              {
                key: 'hasDocuments',
                renderHeader: '文書設定',
              },
            ]}
            renderRow={set => ({
              rowId: set.uid,
              columnValues: {
                status: {
                  renderColumn: (
                    <Badge
                      sx={{
                        width: '48px',
                        height: '24px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: set.enabled ? 'green.600' : 'gray.400',
                        backgroundColor: set.enabled ? 'green.100' : 'gray.100',
                        fontWeight: '700',
                        fontSize: '10px',
                      }}
                    >
                      {set.enabled ? '有効' : '無効'}
                    </Badge>
                  ),
                  tdProps: {
                    minW: '120px',
                    w: '120px',
                  },
                },
                numberingId: {
                  renderColumn: set.numberingId,
                  tdProps: {
                    minW: '140px',
                    w: '140px',
                    color: !set.enabled ? 'gray.400' : undefined,
                  },
                },
                name: {
                  renderColumn: set.name,
                  tdProps: {
                    minW: '160px',
                    color: !set.enabled ? 'gray.400' : undefined,
                  },
                },
                hasRequiredAgreementForm: {
                  renderColumn: set.hasRequiredAgreementForm ? 'あり' : 'なし',
                  tdProps: {
                    minW: '140px',
                    color: !set.enabled ? 'gray.400' : undefined,
                  },
                },
                hasDocuments: {
                  renderColumn: set.hasDocuments ? (
                    <span color={!set.enabled ? 'gray.400' : undefined}>
                      設定済
                    </span>
                  ) : (
                    <HStack
                      spacing="2"
                      color={!set.enabled ? 'gray.400' : 'yellow.500'}
                    >
                      <span>未設定</span>
                      <MessageWarning />
                    </HStack>
                  ),
                  tdProps: {
                    minW: '120px',
                    color: !set.enabled ? 'gray.400' : undefined,
                  },
                },
              },
            })}
            link={set =>
              getIcfDocumentSetDetailRoute({
                trialUid: selectedTrial.uid,
                icfDocumentSetUid: set.uid,
                trialHospitalUid: selectedTrialHospitalUid,
              })
            }
            linkTarget="name"
          />
        ) : (
          <LoadingDots />
        )}
      </Box>
    </Flex>
  )
}
